import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileText, AlertTriangle, CheckCircle, X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import ImageZoomModal from '@/components/ImageZoomModal';
import UpgradeModal from '@/components/UpgradeModal';
import AiConsentDialog from '@/components/AiConsentDialog';
import { useAuth } from '@/contexts/AuthContext';
import { getForeignRates } from '@/utils/vatForeign';

export function ReceiptUpload({ onDataExtracted, isOpen, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [processingResult, setProcessingResult] = useState(null);
  const [thumbnailDialogOpen, setThumbnailDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  // Juli 2026 — KI-Consent proaktiv beim ersten Upload erfragen (User-Feedback:
  // die Onboarding-Checkbox wird übersehen). Datei wird gestasht und nach der
  // Entscheidung verarbeitet (Zustimmung → KI, sonst → OCR).
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const consentGivenRef = React.useRef(false);

  // Feb 2026 — Pro+ai_consent → KI bevorzugen statt nur OCR (Synergie zwischen Wizards)
  const [aiConsent, setAiConsent] = useState(null);
  React.useEffect(() => {
    if (!isOpen) return;
    api.get('/user/ai-consent').then((r) => setAiConsent(r.data?.ai_consent === true)).catch(() => setAiConsent(false));
  }, [isOpen]);

  const isPro = (() => {
    const plan = user?.plan || 'free';
    return ['pro', 'pro_monthly', 'pro_yearly', 'lifetime'].includes(plan);
  })();
  const canUseAi = isPro && aiConsent === true;

  const resetPageNavigation = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const processFile = useCallback(async (file, aiOverride = null) => {
    const useAi = aiOverride === null ? canUseAi : aiOverride;
    // Validate file
    const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) {
      toast.error('Dateityp nicht unterstützt. Erlaubt: PDF, JPG, PNG, HEIC, WEBP');
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error('Datei zu groß. Maximum: 25MB');
      return;
    }

    setIsProcessing(true);
    setProcessingResult(null);
    resetPageNavigation();

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Run AI/OCR und file upload parallel.
      // Feb 2026 — Synergie: wenn Pro+ai_consent, KI nutzen (gleiche Qualität wie ExpenseDetail-AI-Re-Analyse).
      const uploadForm = new FormData();
      uploadForm.append('file', file);

      const extractionEndpoint = useAi ? '/expenses/analyze-receipt-ai' : '/expenses/process-receipt';
      const [extractResponse, uploadResponse] = await Promise.allSettled([
        api.post(extractionEndpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
        api.post('/expenses/upload-receipt', uploadForm, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
      ]);

      // Process extraction result
      const result = extractResponse.status === 'fulfilled' ? extractResponse.value.data : { status: 'error', extracted_data: null };

      // Mark extraction method
      if (result.extracted_data) {
        result.extracted_data._extraction_method = useAi ? 'ai' : 'ocr';
      }

      // Process upload result
      if (uploadResponse.status === 'fulfilled') {
        const uploadData = uploadResponse.value.data;
        result._receiptStorage = {
          file_key: uploadData.file_key,
          filename: uploadData.filename,
          content_type: uploadData.content_type,
          storage_uri: uploadData.storage_uri,
        };
      } else {
        console.error('Receipt file storage failed:', uploadResponse.reason);
        toast.error('Beleg konnte nicht gespeichert werden.');
      }

      setProcessingResult(result);

      if (result.status === 'success' && result.extracted_data) {
        toast.success(useAi ? 'Beleg per KI verarbeitet' : 'Beleg per OCR verarbeitet');
      } else if (extractResponse.status === 'rejected') {
        // Extraction failed but file was uploaded
        if (uploadResponse.status === 'fulfilled') {
          setProcessingResult({ ...result, _receiptStorage: result._receiptStorage, status: 'partial' });
          toast.warning('Daten konnten nicht ausgelesen werden, Beleg wurde aber gespeichert.');
        } else {
          toast.error('Fehler beim Verarbeiten des Belegs');
        }
      } else {
        toast.warning('Beleg verarbeitet, aber keine Daten erkannt');
      }
    } catch (error) {
      console.error('Receipt processing error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Anmeldung erforderlich. Bitte loggen Sie sich erneut ein.');
      } else if (error.response?.status === 403) {
        setUpgradeMessage(error.response?.data?.detail || 'Beleg-Upload erfordert Basic oder Pro Plan');
        setUpgradeModalOpen(true);
      } else if (error.response?.status === 413) {
        toast.error('Datei zu groß. Maximum: 25MB');
      } else {
        toast.error('Fehler beim Verarbeiten des Belegs');
      }
    } finally {
      setIsProcessing(false);
    }
  }, [resetPageNavigation, canUseAi]);

  // Juli 2026 — beim ersten Upload ohne Consent-Entscheidung: Dialog zeigen, Datei stashen
  const maybeAskConsent = useCallback((file, consentValue) => {
    let prompted = false;
    try { prompted = localStorage.getItem('ivoliq_ai_consent_prompted') === '1'; } catch { /* noop */ }
    if (isPro && consentValue === false && !prompted) {
      try { localStorage.setItem('ivoliq_ai_consent_prompted', '1'); } catch { /* noop */ }
      consentGivenRef.current = false;
      setPendingFile(file);
      setConsentDialogOpen(true);
      return true;
    }
    return false;
  }, [isPro]);

  // Aug 2026 — Race-Fix: aiConsent wird async geladen; wer schnell droppt, landete
  // fälschlich bei OCR. Jetzt wird der Consent-Status vor der Endpoint-Wahl aufgelöst.
  const resolveConsent = useCallback(async () => {
    if (aiConsent !== null) return aiConsent;
    try {
      const r = await api.get('/user/ai-consent');
      const v = r.data?.ai_consent === true;
      setAiConsent(v);
      return v;
    } catch {
      setAiConsent(false);
      return false;
    }
  }, [aiConsent]);

  const handleIncomingFile = useCallback(async (file) => {
    const consent = await resolveConsent();
    if (maybeAskConsent(file, consent)) return;
    await processFile(file, isPro && consent);
  }, [resolveConsent, maybeAskConsent, processFile, isPro]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleIncomingFile(e.dataTransfer.files[0]);
    }
  }, [handleIncomingFile]);

  const handleFileSelect = useCallback(async (e) => {
    if (e.target.files && e.target.files[0]) {
      await handleIncomingFile(e.target.files[0]);
    }
  }, [handleIncomingFile]);

  const getConfidenceColor = () => 'bg-gray-100 text-gray-800';
  const getConfidenceIcon = () => null;

  const handleUseData = () => {
    if (processingResult?.extracted_data) {
      const data = { ...processingResult.extracted_data };
      // Attach receipt file storage info so the expense saves it
      if (processingResult._receiptStorage) {
        data._receiptStorage = processingResult._receiptStorage;
      }
      onDataExtracted(data);
      onClose();
    }
  };

  const handleThumbnailClick = () => {
    if (getCurrentThumbnail()) {
      setThumbnailDialogOpen(true);
    }
  };

  const getCurrentThumbnail = () => {
    if (processingResult?.thumbnails && processingResult.thumbnails.length > 0) {
      const thumbnail = processingResult.thumbnails.find(t => t.page === currentPage);
      // P0 FIX: Debug logging for thumbnail retrieval (DEV only)
      if (process.env.NODE_ENV === 'development') {
        console.log('[ReceiptUpload] getCurrentThumbnail - currentPage:', currentPage, 'found:', !!thumbnail);
        console.log('[ReceiptUpload] Available pages:', processingResult.thumbnails.map(t => t.page));
      }
      return thumbnail?.thumbnail_base64;
    }
    // Backward compatibility
    return processingResult?.thumbnail;
  };

  const getTotalPages = () => {
    return processingResult?.total_pages || 1;
  };

  const handlePreviousPage = () => {
    setCurrentPage(prev => {
      const newPage = Math.max(1, prev - 1);
      // P0 FIX: Debug logging for multi-page navigation (DEV only)
      if (process.env.NODE_ENV === 'development') {
        console.log('[ReceiptUpload] Previous page:', prev, '->', newPage, 'Total pages:', getTotalPages());
        console.log('[ReceiptUpload] Current thumbnail:', getCurrentThumbnail()?.substring(0, 50) + '...');
      }
      return newPage;
    });
  };

  const handleNextPage = () => {
    setCurrentPage(prev => {
      const newPage = Math.min(getTotalPages(), prev + 1);
      // P0 FIX: Debug logging for multi-page navigation (DEV only)
      if (process.env.NODE_ENV === 'development') {
        console.log('[ReceiptUpload] Next page:', prev, '->', newPage, 'Total pages:', getTotalPages());
        console.log('[ReceiptUpload] Current thumbnail:', getCurrentThumbnail()?.substring(0, 50) + '...');
      }
      return newPage;
    });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Beleg hochladen
            <Badge variant="outline" className={canUseAi ? "text-emerald-700 border-emerald-300" : "text-slate-600 border-slate-300"} data-testid="extraction-method-badge">
              {canUseAi ? "✨ KI-Erkennung" : "OCR (Text-Erkennung)"}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Global Notice */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Bitte prüfen:</strong> Überprüfen Sie alle automatisch erkannten Daten auf Richtigkeit vor dem Speichern.
            </AlertDescription>
          </Alert>

          {/* Currency Conversion Warning Banner */}
          {processingResult?.extracted_data?.should_show_warning && (
            <Alert className="border-orange-200 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                <div className="whitespace-pre-line">
                  {processingResult.extracted_data.warning_banner_text}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Upload Area */}
          {!processingResult && (
            <Card>
              <CardContent className="pt-6">
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    dragActive
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${isProcessing ? 'pointer-events-none opacity-50' : ''}`}
                >
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="receipt-file-input"
                    disabled={isProcessing}
                  />
                  <label htmlFor="receipt-file-input" className="cursor-pointer">
                    <div className="flex flex-col items-center space-y-4">
                      <Upload className={`h-12 w-12 ${isProcessing ? 'animate-pulse' : 'text-gray-400'}`} />
                      {isProcessing ? (
                        <div className="space-y-2">
                          <p className="text-lg font-semibold">Beleg wird verarbeitet...</p>
                          <p className="text-sm text-gray-500">Dies kann einen Moment dauern</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-lg font-semibold">Beleg hier ablegen oder klicken</p>
                          <p className="text-sm text-gray-500">PDF, JPG, PNG, HEIC, WEBP – max. 25MB</p>
                          <p className="text-xs text-gray-400">Rechnungen, Quittungen, Belege</p>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Processing Results */}
          {processingResult && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Erkannte Daten</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setProcessingResult(null);
                    resetPageNavigation();
                  }}
                >
                  Neuen Beleg hochladen
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Thumbnail with Multi-Page Navigation */}
                {getCurrentThumbnail() && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Vorschau</CardTitle>
                        {getTotalPages() > 1 && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handlePreviousPage}
                              disabled={currentPage <= 1}
                              className="h-7 w-7 p-0"
                            >
                              <ChevronLeft className="h-3 w-3" />
                            </Button>
                            <span className="text-xs text-gray-600 min-w-[4rem] text-center">
                              Seite {currentPage} von {getTotalPages()}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleNextPage}
                              disabled={currentPage >= getTotalPages()}
                              className="h-7 w-7 p-0"
                            >
                              <ChevronRight className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div 
                        className="relative cursor-pointer group"
                        onClick={handleThumbnailClick}
                      >
                        <img
                          key={`page-${currentPage}`} // P0 FIX: Force re-render when page changes
                          src={getCurrentThumbnail()}
                          alt={`Beleg Vorschau - Seite ${currentPage}`}
                          className="w-full h-auto max-h-48 object-contain border rounded"
                          onLoad={() => {
                            // P0 FIX: Debug logging when image loads (DEV only)
                            if (process.env.NODE_ENV === 'development') {
                              console.log('[ReceiptUpload] Image loaded for page:', currentPage);
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                          <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      {getTotalPages() > 1 && (
                        <div className="mt-2 space-y-1">
                          {/* P0 FIX: Explicit page indicator */}
                          <div className="flex justify-center">
                            <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                              Seite {currentPage} von {getTotalPages()}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 text-center">
                            Klicken zum Vergrößern • Navigation mit Pfeiltasten
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Extracted Data */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Extrahierte Felder</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="ai-extracted-check-notice">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Bitte prüfe die erkannten Werte vor dem Übernehmen — die KI-Erkennung ist nicht immer fehlerfrei.
                      </span>
                    </div>
                    {/* Amount */}
                    <div className="flex items-center justify-between p-2 border rounded">
                      <div>
                        <p className="text-sm font-medium">Betrag</p>
                        <p className="text-lg font-bold">
                          {processingResult.extracted_data?.amount 
                            ? `${processingResult.extracted_data.amount.toFixed(2)} ${processingResult.extracted_data.base_currency || '€'}`
                            : 'Nicht erkannt'
                          }
                        </p>
                        {processingResult.extracted_data?.is_foreign_currency && (
                          <p className="text-xs text-orange-600 mt-1">
                            ⚠️ Fremdwährung konvertiert
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center justify-between p-2 border rounded">
                      <div>
                        <p className="text-sm font-medium">Datum</p>
                        <p className="text-sm">
                          {processingResult.extracted_data?.date || 'Nicht erkannt'}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="flex items-center justify-between p-2 border rounded">
                      <div className="flex-1">
                        <p className="text-sm font-medium">Beschreibung</p>
                        <p className="text-sm break-words">
                          {processingResult.extracted_data?.description || 'Nicht erkannt'}
                        </p>
                      </div>
                    </div>

                    {/* Multi-Steuersatz (mehrere MwSt-Sätze auf einem Beleg) */}
                    {Array.isArray(processingResult.extracted_data?.tax_breakdown) &&
                     processingResult.extracted_data.tax_breakdown.filter((t) => t && ((t.net ?? 0) > 0 || (t.vat ?? 0) > 0)).length >= 2 ? (
                      <div className="p-2 border rounded bg-blue-50" data-testid="receipt-tax-breakdown">
                        <p className="text-sm font-medium mb-1">MwSt-Sätze (mehrere erkannt)</p>
                        {processingResult.extracted_data.tax_breakdown
                          .filter((t) => t && ((t.net ?? 0) > 0 || (t.vat ?? 0) > 0))
                          .map((t, i) => (
                            <div key={i} className="flex items-center justify-between text-sm py-0.5">
                              <span className="font-medium">{Number(t.rate)}%</span>
                              <span className="text-xs text-gray-600">
                                netto {Number(t.net || 0).toFixed(2)} · MwSt {Number(t.vat || 0).toFixed(2)} {processingResult.extracted_data.base_currency || '€'}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (processingResult.extracted_data?.vat_rate ?? 0) > 0 ? (
                      <div className="flex items-center justify-between p-2 border rounded bg-blue-50">
                        <div>
                          <p className="text-sm font-medium">MwSt.-Satz</p>
                          <p className="text-sm">
                            {processingResult.extracted_data.vat_rate}%
                          </p>
                          {(processingResult.extracted_data?.vat_amount ?? 0) > 0 ? (
                            <p className="text-xs text-gray-600 mt-1">
                              MwSt.-Betrag: {processingResult.extracted_data.vat_amount.toFixed(2)} {processingResult.extracted_data.base_currency || '€'}
                            </p>
                          ) : null}
                          {(processingResult.extracted_data?.net_amount ?? 0) > 0 ? (
                            <p className="text-xs text-gray-600">
                              Netto: {processingResult.extracted_data.net_amount.toFixed(2)} {processingResult.extracted_data.base_currency || '€'}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    {/* Auslands-USt-Hinweis: Beleg mit ausländischem Steuersatz */}
                    {(() => {
                      const foreignRates = getForeignRates(processingResult?.extracted_data, user?.country);
                      if (!foreignRates.length) return null;
                      const cc = (user?.country || 'DE').toUpperCase();
                      const ratesStr = foreignRates.map((r) => `${r}%`).join(', ');
                      return (
                        <Alert className="mt-2 border-amber-300 bg-amber-50" data-testid="receipt-foreign-vat-hint">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          <AlertDescription className="text-xs text-amber-800">
                            Diese Rechnung weist einen <strong>ausländischen Steuersatz</strong> aus ({ratesStr}).
                            Diese USt ist <strong>nicht</strong> über deine {cc}-Voranmeldung als Vorsteuer abziehbar
                            (der Bruttobetrag zählt als Betriebsausgabe; ggf. EU-Vorsteuererstattungsverfahren).
                            Sollte die Leistung eigentlich dem <strong>Reverse-Charge</strong> unterliegen, kann der
                            Lieferant die Rechnung berichtigen (netto, ohne USt).
                          </AlertDescription>
                        </Alert>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>

              {/* Debug Information (only shown in development or when debug is enabled) */}
              {processingResult?.debug_info && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-sm text-orange-600">Debug Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-xs">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="font-medium">Extraction Method:</p>
                        <p className="text-gray-600">{processingResult.debug_info.extraction_method}</p>
                      </div>
                      <div>
                        <p className="font-medium">Text Length:</p>
                        <p className="text-gray-600">{processingResult.debug_info.raw_text_length} characters</p>
                      </div>
                    </div>
                    
                    {processingResult.debug_info.pattern_matches && (
                      <div>
                        <p className="font-medium">Pattern Matches:</p>
                        <div className="bg-gray-50 p-2 rounded text-xs">
                          <p>Amounts: {processingResult.debug_info.pattern_matches.amounts_found?.join(', ') || 'None'}</p>
                          <p>Dates: {processingResult.debug_info.pattern_matches.dates_found?.join(', ') || 'None'}</p>
                          <p>Currencies: {processingResult.debug_info.pattern_matches.currencies_found?.join(', ') || 'None'}</p>
                          <p>Companies: {processingResult.debug_info.pattern_matches.company_indicators?.join(', ') || 'None'}</p>
                        </div>
                      </div>
                    )}
                    
                    {processingResult.debug_info.extraction_issues?.length > 0 && (
                      <div>
                        <p className="font-medium text-red-600">Extraction Issues:</p>
                        <ul className="list-disc list-inside text-red-600">
                          {processingResult.debug_info.extraction_issues.map((issue, index) => (
                            <li key={index}>{issue.replace(/_/g, ' ')}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {processingResult.debug_info.confidence_breakdown && (
                      <div>
                        <p className="font-medium">Confidence Breakdown:</p>
                        <div className="bg-gray-50 p-2 rounded text-xs">
                          <p>Overall: {processingResult.debug_info.confidence_breakdown.overall_confidence?.toFixed(1)}%</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={onClose}>
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleUseData}
                  disabled={!processingResult.extracted_data}
                >
                  Daten übernehmen
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Image Zoom Modal with Multi-Page Support */}
        <ImageZoomModal
          isOpen={thumbnailDialogOpen}
          onClose={() => setThumbnailDialogOpen(false)}
          imageUrl={getCurrentThumbnail()}
          title="Beleg Vorschau"
          // Multi-page support
          thumbnails={processingResult?.thumbnails}
          totalPages={getTotalPages()}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
        </DialogContent>
      </Dialog>
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        message={upgradeMessage}
        currentPlan={user?.plan || 'free'}
      />
      {/* Juli 2026 — KI-Consent beim ersten Upload; danach gestashte Datei verarbeiten */}
      <AiConsentDialog
        isOpen={consentDialogOpen}
        onClose={() => {
          setConsentDialogOpen(false);
          const f = pendingFile;
          const consented = consentGivenRef.current;
          setPendingFile(null);
          consentGivenRef.current = false;
          if (f) processFile(f, consented);
        }}
        onConsented={() => {
          consentGivenRef.current = true;
          setAiConsent(true);
        }}
      />
    </>
  );
}

export default ReceiptUpload;